import { ref } from 'vue'
import type { Workbox } from 'workbox-window'
import type { BeforeInstallPromptEvent } from './types'

const needsRefresh = ref(false)
const offlineReady = ref(false)
const canInstall = ref(false)
const isInstalled = ref(false)

let deferredPrompt: BeforeInstallPromptEvent | null = null
let wb: Workbox | undefined

export function usePwa(): {
  needsRefresh: typeof needsRefresh
  offlineReady: typeof offlineReady
  canInstall: typeof canInstall
  isInstalled: typeof isInstalled
  registerSW: () => Promise<void>
  captureInstallPrompt: (event: BeforeInstallPromptEvent) => void
  promptInstall: () => Promise<void>
  updateServiceWorker: () => void
} {
  const registerSW = async (): Promise<void> => {
    if ('serviceWorker' in navigator) {
      const { Workbox } = await import('workbox-window')

      wb = new Workbox('/sw.js', { scope: '/' })

      wb.addEventListener('installed', event => {
        if (event.isUpdate) {
          needsRefresh.value = true
        } else {
          offlineReady.value = true
        }
      })

      await wb.register()
    }
  }

  const captureInstallPrompt = (event: BeforeInstallPromptEvent): void => {
    event.preventDefault()
    deferredPrompt = event
    canInstall.value = true
  }

  const promptInstall = async (): Promise<void> => {
    if (!deferredPrompt) {
      return
    }

    await deferredPrompt.prompt()
    const { outcome } = await deferredPrompt.userChoice

    if (outcome === 'accepted') {
      isInstalled.value = true
    }

    deferredPrompt = null
    canInstall.value = false
  }

  const updateServiceWorker = (): void => {
    if (wb) {
      wb.messageSkipWaiting()
    }
    window.location.reload()
  }

  return {
    needsRefresh,
    offlineReady,
    canInstall,
    isInstalled,
    registerSW,
    captureInstallPrompt,
    promptInstall,
    updateServiceWorker,
  }
}
